import {
  VolumeTypeEnum,
  type QuarterVolume,
  type SecureVolumeContainer,
} from "../../../../store/types/secureVolume.types";

// ── Year extraction ─────────────────────────────────────────────────────

export function extractYears(facilities: SecureVolumeContainer[]): number[] {
  const years = new Set<number>();
  for (const f of facilities) {
    for (const v of f.QuarterVolumes) years.add(v.Year);
  }
  return Array.from(years).sort((a, b) => a - b);
}

// ── Volume lookups ──────────────────────────────────────────────────────

export function getQuarterVolumesByType(
  volumes: QuarterVolume[],
  year: number,
  quarter: number,
): Map<VolumeTypeEnum, number> {
  const map = new Map<VolumeTypeEnum, number>();
  for (const v of volumes) {
    if (v.Year === year && v.Quarter === quarter) {
      map.set(v.VolumeType, (map.get(v.VolumeType) ?? 0) + v.Quantity);
    }
  }
  return map;
}

export function getYearlyTypeMap(
  volumes: QuarterVolume[],
  year: number,
): Map<VolumeTypeEnum, number> {
  const map = new Map<VolumeTypeEnum, number>();
  for (const v of volumes) {
    if (v.Year === year) {
      map.set(v.VolumeType, (map.get(v.VolumeType) ?? 0) + v.Quantity);
    }
  }
  return map;
}

export function getQuarterForecast(
  volumes: QuarterVolume[],
  year: number,
  quarter: number,
): number {
  return volumes
    .filter(
      (v) =>
        v.Year === year &&
        v.Quarter === quarter &&
        v.VolumeType === VolumeTypeEnum.Forecast,
    )
    .reduce((sum, v) => sum + v.Quantity, 0);
}

export function getYearlyForecast(
  volumes: QuarterVolume[],
  year: number,
): number {
  return volumes
    .filter((v) => v.Year === year && v.VolumeType === VolumeTypeEnum.Forecast)
    .reduce((sum, v) => sum + v.Quantity, 0);
}

// ── Input value helpers ─────────────────────────────────────────────────

export function getInputValue(
  keyPrefix: string,
  year: number,
  quarter: number,
  volumes: QuarterVolume[],
  inputValues: Record<string, string>,
): string {
  const key = `${keyPrefix}-${year}-${quarter}`;
  if (key in inputValues) return inputValues[key];
  const existing = volumes.find(
    (v) =>
      v.Year === year &&
      v.Quarter === quarter &&
      v.VolumeType === VolumeTypeEnum.InProgress,
  );
  return existing ? existing.Quantity.toString() : "";
}

// ── Conversion helpers ──────────────────────────────────────────────────

export function kwhFromPercent(percent: number, forecast: number): number {
  const raw = (forecast * percent) / 100;
  const step = getRoundingStep(forecast);
  return Math.round(raw / step) * step;
}

export function percentFromKwh(kwh: number, forecast: number): number {
  if (forecast === 0) return 0;
  return (kwh / forecast) * 100;
}

// ── Computed totals ─────────────────────────────────────────────────────

export function computeFacilityTotal(
  keyPrefix: string,
  year: number,
  volumes: QuarterVolume[],
  inputValues: Record<string, string>,
): number {
  return [1, 2, 3, 4].reduce((sum, q) => {
    const val = getInputValue(keyPrefix, year, q, volumes, inputValues);
    return sum + (Number(val) || 0);
  }, 0);
}

export function computeGrandTotal(
  facilities: SecureVolumeContainer[],
  year: number,
  inputValues: Record<string, string>,
): number {
  return facilities.reduce((sum, facility, index) => {
    const keyPrefix = `f${index}`;
    return (
      sum +
      computeFacilityTotal(keyPrefix, year, facility.QuarterVolumes, inputValues)
    );
  }, 0);
}

export function computeGrandQuarterTotal(
  facilities: SecureVolumeContainer[],
  year: number,
  quarter: number,
  inputValues: Record<string, string>,
): number {
  return facilities.reduce((sum, facility, index) => {
    const val = getInputValue(`f${index}`, year, quarter, facility.QuarterVolumes, inputValues);
    return sum + (Number(val) || 0);
  }, 0);
}

export function getGrandQuarterSecuredPercentage(
  facilities: SecureVolumeContainer[],
  year: number,
  quarter: number,
  inputValues: Record<string, string>,
): number | null {
  let totalForecast = 0;
  let totalInput = 0;

  for (let i = 0; i < facilities.length; i++) {
    const f = facilities[i];
    totalForecast += getQuarterForecast(f.QuarterVolumes, year, quarter);
    const val = getInputValue(`f${i}`, year, quarter, f.QuarterVolumes, inputValues);
    totalInput += Number(val) || 0;
  }

  if (totalForecast === 0) return null;
  return (totalInput / totalForecast) * 100;
}

// ── Secured percentage ──────────────────────────────────────────────────

export function getSecuredPercentage(
  volumes: QuarterVolume[],
  year: number,
  inputValues?: Record<string, string>,
  keyPrefix?: string,
): number | null {
  const forecast = getYearlyForecast(volumes, year);
  const consumption = volumes
    .filter((v) => v.Year === year && v.VolumeType === VolumeTypeEnum.Consumption)
    .reduce((s, v) => s + v.Quantity, 0);
  const denominator = forecast + consumption;
  if (denominator === 0) return null;

  let secured: number;
  if (inputValues && keyPrefix) {
    secured = [1, 2, 3, 4].reduce(
      (s, q) => s + (Number(getInputValue(keyPrefix, year, q, volumes, inputValues)) || 0),
      0,
    );
  } else {
    secured = volumes
      .filter(
        (v) => v.Year === year && v.VolumeType === VolumeTypeEnum.Secured,
      )
      .reduce((s, v) => s + v.Quantity, 0);
  }

  return (secured / denominator) * 100;
}

export function getGrandSecuredPercentage(
  facilities: SecureVolumeContainer[],
  year: number,
  inputValues: Record<string, string>,
): number | null {
  let totalForecast = 0;
  let totalConsumption = 0;
  let totalInput = 0;

  for (let i = 0; i < facilities.length; i++) {
    const f = facilities[i];
    const keyPrefix = `f${i}`;
    totalForecast += getYearlyForecast(f.QuarterVolumes, year);
    totalConsumption += f.QuarterVolumes
      .filter((v) => v.Year === year && v.VolumeType === VolumeTypeEnum.Consumption)
      .reduce((s, v) => s + v.Quantity, 0);
    for (let q = 1; q <= 4; q++) {
      const val = getInputValue(
        keyPrefix,
        year,
        q,
        f.QuarterVolumes,
        inputValues,
      );
      totalInput += Number(val) || 0;
    }
  }

  const denominator = totalForecast + totalConsumption;
  if (denominator === 0) return null;
  return (totalInput / denominator) * 100;
}

export function getQuarterSecuredPercentage(
  volumes: QuarterVolume[],
  year: number,
  quarter: number,
): number | null {
  const forecast = volumes
    .filter(
      (v) => v.Year === year && v.Quarter === quarter && v.VolumeType === VolumeTypeEnum.Forecast,
    )
    .reduce((s, v) => s + v.Quantity, 0);
  const consumption = volumes
    .filter(
      (v) => v.Year === year && v.Quarter === quarter && v.VolumeType === VolumeTypeEnum.Consumption,
    )
    .reduce((s, v) => s + v.Quantity, 0);
  const denominator = forecast + consumption;
  if (denominator === 0) return null;

  const secured = volumes
    .filter(
      (v) => v.Year === year && v.Quarter === quarter && v.VolumeType === VolumeTypeEnum.Secured,
    )
    .reduce((s, v) => s + v.Quantity, 0);

  return (secured / denominator) * 100;
}

// ── Distribution by historical secured pattern ──────────────────────────

/** Avrundningssteg – alltid 100. */
function getRoundingStep(_total: number): number {
  return 100;
}

/** Fördela en total proportionellt efter vikter, avrundat med dynamiskt steg.
 *  Lägger eventuell rest på det största kvartalet. */
function distributeByWeights(
  total: number,
  weights: number[],
  weightTotal: number,
): Record<number, number> {
  const step = getRoundingStep(total);
  const result: Record<number, number> = {};
  for (let q = 1; q <= 4; q++) {
    result[q] = Math.round((total * weights[q - 1]) / weightTotal / step) * step;
  }
  // Justera rest på det kvartal med störst vikt
  const sum = result[1] + result[2] + result[3] + result[4];
  const diff = Math.round((total - sum) / step) * step;
  if (diff !== 0) {
    const maxQ = weights.indexOf(Math.max(...weights)) + 1;
    result[maxQ] += diff;
  }
  return result;
}

export function distributeTotalByHistory(
  total: number,
  volumes: QuarterVolume[],
  year: number,
): Record<number, number> {
  // 1. Försök fördela efter närmaste års säkrade mönster (sök bakåt)
  const allYears = [...new Set(volumes.map((v) => v.Year))].sort((a, b) => b - a);
  for (const prevYear of allYears) {
    if (prevYear >= year) continue;
    const prevSecured = [1, 2, 3, 4].map((q) =>
      volumes
        .filter(
          (v) =>
            v.Year === prevYear &&
            v.Quarter === q &&
            v.VolumeType === VolumeTypeEnum.Secured,
        )
        .reduce((s, v) => s + v.Quantity, 0),
    );
    const prevSecuredTotal = prevSecured.reduce((s, v) => s + v, 0);

    if (prevSecuredTotal > 0) {
      return distributeByWeights(total, prevSecured, prevSecuredTotal);
    }
  }

  // 2. Fallback: fördela efter prognos
  const forecasts = [1, 2, 3, 4].map((q) =>
    getQuarterForecast(volumes, year, q),
  );
  const forecastTotal = forecasts.reduce((s, v) => s + v, 0);

  if (forecastTotal > 0) {
    return distributeByWeights(total, forecasts, forecastTotal);
  }

  // 3. Sista fallback: jämn fördelning
  const step = getRoundingStep(total);
  const perQ = Math.round(total / 4 / step) * step;
  const result: Record<number, number> = {};
  for (let q = 1; q <= 4; q++) {
    result[q] = perQ;
  }
  const diff = Math.round((total - perQ * 4) / step) * step;
  if (diff !== 0) result[1] += diff;
  return result;
}
